/**
 * FounderSignature — Terri's personal mark.
 *
 * Her first name in Caveat with a small tangerine SVG flourish
 * underneath (the same handwritten squiggle path used by
 * `<EmSwash>`). Meant to feel signed by her, not styled by us.
 *
 * Deployable at four sizes for different surfaces:
 *   xs (32px) — inline attribution, email footers, comments
 *   sm (48px) — Home footer sign-off under the "Join the family" CTA
 *   md (64px) — Team page pull-quote attribution
 *   lg (96px) — standalone "letter from the founder" moments
 *
 * Optional `prefix` prop renders a small handwritten line ABOVE the
 * signature ("with love,", "sincerely,", etc.) for letter-style
 * contexts. Optional `role` renders a small typed line BELOW.
 *
 * The signature is a *visual* mark — attribution semantics should be
 * provided by the caller (wrap in `<cite>` or a `<blockquote>` when
 * used as pull-quote attribution).
 *
 * Zero external dependencies: it's Caveat + a tangerine SVG path.
 */

const SIZES = {
  xs: {
    name: "text-3xl",
    flourish: "w-20 h-2",
    flourishMargin: "mt-0.5",
    prefix: "text-base",
    role: "text-[13px] mt-1.5",
  },
  sm: {
    name: "text-[3.5rem]",
    flourish: "w-28 h-2.5",
    flourishMargin: "mt-1",
    prefix: "text-base",
    role: "text-[13px] mt-2",
  },
  md: {
    name: "text-[3.5rem]",
    flourish: "w-40 h-3",
    flourishMargin: "mt-1.5",
    prefix: "text-3xl",
    role: "text-[13px] mt-2.5",
  },
  lg: {
    name: "text-[3.5rem] lg:text-[5.5rem]",
    flourish: "w-56 h-3.5",
    flourishMargin: "mt-2",
    prefix: "text-3xl",
    role: "text-[13px] mt-3",
  },
};

export const FounderSignature = ({
  size = "sm",
  prefix,        // optional handwritten line above (e.g. "with love,")
  role,          // optional typed line below (e.g. "founder · my rich aunt")
  className = "",
}) => {
  const s = SIZES[size] || SIZES.sm;
  return (
    <span
      data-testid="founder-signature"
      className={`inline-flex flex-col items-start leading-none ${className}`}
    >
      {prefix && (
        <span
          aria-hidden="true"
          className={`mb-1.5 origin-left -rotate-[1.5deg] font-caveat leading-none text-olive ${s.prefix}`}
        >
          {prefix}
        </span>
      )}
      <span
        className={`origin-left -rotate-[1deg] font-caveat font-medium leading-[0.9] text-ink ${s.name}`}
      >
        Terri
      </span>
      <svg
        aria-hidden="true"
        viewBox="0 0 200 16"
        preserveAspectRatio="none"
        className={`ml-1 origin-left -rotate-[1deg] text-tangerine ${s.flourish} ${s.flourishMargin}`}
      >
        <path
          d="M4,12 Q40,3 100,10 T196,8"
          stroke="currentColor"
          strokeWidth="3.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {role && (
        <span
          className={`ml-0.5 font-semibold uppercase tracking-[0.18em] text-olive ${s.role}`}
        >
          {role}
        </span>
      )}
    </span>
  );
};
